import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Grid, Tab, Tabs, Typography, CircularProgress, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton, Avatar, Box, Tooltip, Pagination,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import {
  sendFamilyMemberRequestAPI,
  respondFamilyMemberRequestAPI,
  getMyFamilyMembersAPI,
  getIncomingFamilyRequestsAPI,
  getSentFamilyRequestsAPI,
  removeFamilyMemberAPI,
} from '../../services/mainService';
import { StyledSecondaryButton } from '../../assets/styles/CommonStyles';
import AppLayout from '../SideBar';

const RELATIONS = ['Parent', 'Child', 'Spouse', 'Sibling', 'Guardian', 'Other'];
const PAGE_SIZE = 5;

const RELATION_COLORS = {
  Parent:   { background: '#EBF5FB', color: '#1A5276', border: '#AED6F1' },
  Child:    { background: '#E8F8F2', color: '#1E8449', border: '#A9DFBF' },
  Spouse:   { background: '#FEF9E7', color: '#9A7D0A', border: '#F9E79F' },
  Sibling:  { background: '#F4ECF7', color: '#7D3C98', border: '#D7BDE2' },
  Guardian: { background: '#FBEEE6', color: '#BA4A00', border: '#F0B27A' },
  Other:    { background: '#F2F3F4', color: '#616A6B', border: '#D5D8DC' },
};

const STATUS_COLORS = {
  accepted: { background: '#E8F8F2', color: '#1E8449', border: '#A9DFBF' },
  rejected: { background: '#FDEDEC', color: '#C0392B', border: '#F1948A' },
  pending:  { background: '#FEF9E7', color: '#9A7D0A', border: '#F9E79F' },
};

const Tag = ({ label, colorMap }) => {
  const style = colorMap[label] || colorMap[Object.keys(colorMap)[0]];
  return (
    <Box component="span" sx={{
      display: 'inline-block',
      px: 1,
      py: '2px',
      borderRadius: '4px',
      fontSize: 11,
      fontWeight: 600,
      lineHeight: '18px',
      background: style.background,
      color: style.color,
      border: `1px solid ${style.border}`,
      userSelect: 'none',
    }}>
      {label}
    </Box>
  );
};

const RelationTag = ({ label }) => <Tag label={label} colorMap={RELATION_COLORS} />;
const StatusTag = ({ label }) => (
  <Tag label={label.charAt(0).toUpperCase() + label.slice(1)} colorMap={STATUS_COLORS} />
);

const Container = styled(Grid)({
  '& .MuiTypography-root': {
    fontFamily: 'Poppins',
    fontStyle: 'normal',
  },
  '& .pagetitle': {
    paddingTop: 70,
    '& .MuiTypography-root': {
      fontWeight: 500,
      fontSize: '32px',
      lineHeight: '42px',
      color: '#2B2B2B',
      '& + .MuiTypography-root': {
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '18px',
        color: '#5E5E5E',
      },
    },
  },
});

const StyledTabs = styled(Tabs)({
  borderBottom: '1.2px solid #F5F5F7',
  '& .MuiTab-root': {
    fontFamily: 'Poppins',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '18px',
    color: '#5E5E5E',
  },
  '& .Mui-selected': {
    background: 'linear-gradient(90deg, #097EB7 0%, #2AAB6C 100%)',
    backgroundClip: 'text',
    textFillColor: 'transparent',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    borderBottom: '1.2px solid',
  },
});

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2, pb: 2 }}>{children}</Box> : null;
}

const SectionHeader = ({ label, accentColor, tooltip }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, borderLeft: `3px solid ${accentColor}`, pl: 1.5, py: 0.25 }}>
    <Typography sx={{ fontWeight: 700, fontSize: 12, color: '#2B2B2B', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
      {label}
    </Typography>
    {tooltip && (
      <Tooltip title={tooltip} placement="right">
        <InfoOutlinedIcon sx={{ fontSize: 15, color: '#9E9E9E', cursor: 'pointer' }} />
      </Tooltip>
    )}
  </Box>
);

const MemberCard = ({ m, onView, onRemove, actionLoading, showView = true }) => (
  <Box sx={{
    border: '1px solid #F0F0F2',
    borderRadius: 2,
    p: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#fff',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    transition: 'all 0.18s',
    '&:hover': { boxShadow: '0 3px 10px rgba(9,126,183,0.10)', borderColor: '#097EB7' },
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
      <Avatar
        src={m.profileImage || ''}
        sx={{ width: 44, height: 44, bgcolor: '#097EB7', flexShrink: 0, fontSize: 18, fontWeight: 700 }}
      >
        {m.name?.[0]?.toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
          {m.name}
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
          {m.email || m.phoneNumber}
        </Typography>
      </Box>
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75, flexShrink: 0 }}>
      <RelationTag label={m.relation} />
      <Box sx={{ display: 'flex', gap: 0.25 }}>
        {showView && (
          <Tooltip title="View Health Data">
            <IconButton size="small" onClick={onView} sx={{ color: '#097EB7' }}>
              <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Remove">
          <span>
            <IconButton size="small" disabled={actionLoading === m.relationId} onClick={onRemove} sx={{ color: '#F36363' }}>
              {actionLoading === m.relationId ? <CircularProgress size={16} /> : <DeleteOutlineIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  </Box>
);

const RequestCard = ({ person, relation, actions, badge }) => (
  <Box sx={{
    border: '1px solid #F0F0F2',
    borderRadius: 2,
    p: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#fff',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    transition: 'all 0.18s',
    '&:hover': { boxShadow: '0 3px 10px rgba(0,0,0,0.08)', borderColor: '#E0E0E0' },
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
      <Avatar src={person?.profileImage || ''} sx={{ width: 44, height: 44, bgcolor: '#2AAB6C', flexShrink: 0, fontSize: 18, fontWeight: 700 }}>
        {person?.name?.[0]?.toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
          {person?.name}
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
          {person?.email || person?.phoneNumber}
        </Typography>
      </Box>
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75, flexShrink: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <RelationTag label={relation} />
        {badge}
      </Box>
      {actions && <Box sx={{ display: 'flex', gap: 0.25 }}>{actions}</Box>}
    </Box>
  </Box>
);

const EmptyState = ({ message, onAdd }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 7, gap: 2 }}>
    <PeopleAltOutlinedIcon sx={{ fontSize: 52, color: '#D5D8DC' }} />
    <Typography color="textSecondary" sx={{ fontSize: 14 }}>{message}</Typography>
    {onAdd && (
      <StyledSecondaryButton variant="contained" startIcon={<PersonAddIcon />} onClick={onAdd} style={{ width: 'auto', padding: '8px 20px' }}>
        Add Member
      </StyledSecondaryButton>
    )}
  </Box>
);

const FamilyMembers = () => {
  const history = useHistory();
  const { userData } = useSelector((state) => ({ userData: state.auth.userData }));
  const token = userData?.token;

  const [tab, setTab] = useState(0);
  const [members, setMembers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ emailOrPhone: '', relation: '' });
  const [addLoading, setAddLoading] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState({ relationId: '', name: '' });

  const [myPage, setMyPage] = useState(1);
  const [ofPage, setOfPage] = useState(1);
  const [reqPage, setReqPage] = useState(1);
  const [sentPage, setSentPage] = useState(1);

  const fetchMembers = async () => {
    setLoading(true);
    const [membersRes, requestsRes, sentRes] = await Promise.all([
      getMyFamilyMembersAPI({ token }),
      getIncomingFamilyRequestsAPI({ token }),
      getSentFamilyRequestsAPI({ token }),
    ]);
    if (membersRes.response_type !== 'fail') { setMembers(membersRes.response.data || []); setMyPage(1); setOfPage(1); }
    if (requestsRes.response_type !== 'fail') { setRequests(requestsRes.response.data || []); setReqPage(1); }
    if (sentRes.response_type !== 'fail') { setSentRequests(sentRes.response.data || []); setSentPage(1); }
    setLoading(false);
  };

  useEffect(() => { fetchMembers(); }, []);

  const handleSendRequest = async () => {
    if (!form.emailOrPhone || !form.relation) {
      toast.error('Please fill all fields.');
      return;
    }
    setAddLoading(true);
    const res = await sendFamilyMemberRequestAPI({ token, ...form });
    setAddLoading(false);
    if (res.response_type !== 'fail') {
      toast.success('Request sent successfully.');
      setAddOpen(false);
      setForm({ emailOrPhone: '', relation: '' });
      fetchMembers();
    }
  };

  const handleRespond = async (requestId, action) => {
    setActionLoading(requestId + action);
    const res = await respondFamilyMemberRequestAPI({ token, requestId, action });
    setActionLoading('');
    if (res.response_type === 'fail') {
      toast.error(res.response?.message || 'Failed to respond.');
    } else {
      toast.success(action === 'accept' ? 'Request accepted.' : 'Request rejected.');
      fetchMembers();
    }
  };

  const handleRemove = (relationId, name) => {
    setConfirmTarget({ relationId, name });
    setConfirmOpen(true);
  };

  const confirmRemove = async () => {
    setConfirmOpen(false);
    setActionLoading(confirmTarget.relationId);
    const res = await removeFamilyMemberAPI({ token, relationId: confirmTarget.relationId });
    setActionLoading('');
    if (res.response_type !== 'fail') {
      toast.success('Family member removed.');
      fetchMembers();
    }
  };

  const myMembers = members.filter(m => m.canViewData);
  const memberOf = members.filter(m => !m.canViewData);

  const pagedMyMembers = myMembers.slice((myPage - 1) * PAGE_SIZE, myPage * PAGE_SIZE);
  const pagedMemberOf = memberOf.slice((ofPage - 1) * PAGE_SIZE, ofPage * PAGE_SIZE);
  const pagedRequests = requests.slice((reqPage - 1) * PAGE_SIZE, reqPage * PAGE_SIZE);
  const pagedSent = sentRequests.slice((sentPage - 1) * PAGE_SIZE, sentPage * PAGE_SIZE);

  return (
    <Grid container spacing={0}>
      <AppLayout>
        <Container container spacing={4}>
          <Grid item xs={12} className="pagetitle">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography>Family Members</Typography>
                <Typography>Manage your family members and their health data access.</Typography>
              </Box>
              <StyledSecondaryButton
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => setAddOpen(true)}
                style={{ width: 'auto', padding: '8px 20px' }}
              >
                Add Member
              </StyledSecondaryButton>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={0} sx={{ paddingBottom: '20px' }}>
              <Paper elevation={1} sx={{ width: '100%' }}>
                <Grid item xs={12}>
                  <StyledTabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant="scrollable"
                    TabIndicatorProps={{ style: { display: 'none' } }}
                  >
                    {[
                      'My Family',
                      `Requests${requests.length ? ` (${requests.length})` : ''}`,
                      `Sent${sentRequests.length ? ` (${sentRequests.length})` : ''}`,
                    ].map((label, i) => (
                      <Tab key={i} label={label} id={`fm-tab-${i}`} aria-controls={`fm-tabpanel-${i}`} />
                    ))}
                  </StyledTabs>
                </Grid>

                <Grid item xs={12} sx={{ px: 2, minHeight: 300 }}>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6, mb: 6 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      {/* ── My Family Tab ── */}
                      <TabPanel value={tab} index={0}>
                        {members.length === 0 ? (
                          <EmptyState
                            message="No family members added yet."
                            onAdd={() => setAddOpen(true)}
                          />
                        ) : (
                          <Grid container spacing={2} sx={{ mt: 0 }}>
                            {/* Left column — My Family Members */}
                            <Grid item xs={12} md={6}>
                              <Box sx={{
                                border: '1px solid #F0F0F2',
                                borderRadius: 2,
                                overflow: 'hidden',
                                height: '100%',
                              }}>
                                <Box sx={{ px: 2, py: 1.5, background: 'linear-gradient(90deg, #EBF5FB 0%, #f8f8f8 100%)', borderBottom: '1px solid #F0F0F2', borderLeft: '3px solid #097EB7' }}>
                                  <Typography sx={{ fontWeight: 700, fontSize: 12, color: '#1A5276', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                                    My Family Members
                                  </Typography>
                                  <Typography sx={{ fontSize: 11, color: '#5E5E5E', mt: 0.25 }}>
                                    You can view their health data
                                  </Typography>
                                </Box>
                                {myMembers.length === 0 ? (
                                  <Box sx={{ py: 5, textAlign: 'center' }}>
                                    <Typography variant="caption" color="textSecondary">No members added yet.</Typography>
                                  </Box>
                                ) : (
                                  <>
                                    {pagedMyMembers.map((m) => (
                                      <Box key={m.relationId} sx={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        px: 2, py: 1.5, borderBottom: '1px solid #F5F5F7',
                                        '&:last-child': { borderBottom: 'none' },
                                        '&:hover': { background: '#FAFCFF' },
                                        transition: 'background 0.15s',
                                      }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                          <Avatar src={m.profileImage || ''} sx={{ width: 38, height: 38, bgcolor: '#097EB7', fontSize: 16, fontWeight: 700, flexShrink: 0 }}>
                                            {m.name?.[0]?.toUpperCase()}
                                          </Avatar>
                                          <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</Typography>
                                            <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{m.email || m.phoneNumber}</Typography>
                                          </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                          <RelationTag label={m.relation} />
                                          <Tooltip title="View Health Data">
                                            <IconButton size="small" onClick={() => history.push(`/patient/family-member/${m._id}/data`)} sx={{ color: '#097EB7' }}>
                                              <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Remove">
                                            <span>
                                              <IconButton size="small" disabled={actionLoading === m.relationId} onClick={() => handleRemove(m.relationId, m.name)} sx={{ color: '#F36363' }}>
                                                {actionLoading === m.relationId ? <CircularProgress size={15} /> : <DeleteOutlineIcon sx={{ fontSize: 18 }} />}
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        </Box>
                                      </Box>
                                    ))}
                                    {myMembers.length > PAGE_SIZE && (
                                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5, borderTop: '1px solid #F5F5F7' }}>
                                        <Pagination
                                          count={Math.ceil(myMembers.length / PAGE_SIZE)}
                                          page={myPage}
                                          onChange={(_, v) => setMyPage(v)}
                                          size="small"
                                          color="primary"
                                        />
                                      </Box>
                                    )}
                                  </>
                                )}
                              </Box>
                            </Grid>

                            {/* Right column — I Am a Member Of */}
                            <Grid item xs={12} md={6}>
                              <Box sx={{
                                border: '1px solid #F0F0F2',
                                borderRadius: 2,
                                overflow: 'hidden',
                                height: '100%',
                              }}>
                                <Box sx={{ px: 2, py: 1.5, background: 'linear-gradient(90deg, #E8F8F2 0%, #f8f8f8 100%)', borderBottom: '1px solid #F0F0F2', borderLeft: '3px solid #2AAB6C', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                  <Box>
                                    <Typography sx={{ fontWeight: 700, fontSize: 12, color: '#1E8449', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                                      I Am a Member Of
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color: '#5E5E5E', mt: 0.25 }}>
                                      These people added you to their family
                                    </Typography>
                                  </Box>
                                  <Tooltip title="You cannot view their health data unless they add you back." placement="left">
                                    <InfoOutlinedIcon sx={{ fontSize: 16, color: '#9E9E9E', cursor: 'pointer', mt: 0.25 }} />
                                  </Tooltip>
                                </Box>
                                {memberOf.length === 0 ? (
                                  <Box sx={{ py: 5, textAlign: 'center' }}>
                                    <Typography variant="caption" color="textSecondary">You are not a member of any family yet.</Typography>
                                  </Box>
                                ) : (
                                  <>
                                    {pagedMemberOf.map((m) => (
                                      <Box key={m.relationId} sx={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        px: 2, py: 1.5, borderBottom: '1px solid #F5F5F7',
                                        '&:last-child': { borderBottom: 'none' },
                                        '&:hover': { background: '#FAFFFC' },
                                        transition: 'background 0.15s',
                                      }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                          <Avatar src={m.profileImage || ''} sx={{ width: 38, height: 38, bgcolor: '#2AAB6C', fontSize: 16, fontWeight: 700, flexShrink: 0 }}>
                                            {m.name?.[0]?.toUpperCase()}
                                          </Avatar>
                                          <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</Typography>
                                            <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{m.email || m.phoneNumber}</Typography>
                                          </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                          <RelationTag label={m.relation} />
                                          <Tooltip title="Remove">
                                            <span>
                                              <IconButton size="small" disabled={actionLoading === m.relationId} onClick={() => handleRemove(m.relationId, m.name)} sx={{ color: '#F36363' }}>
                                                {actionLoading === m.relationId ? <CircularProgress size={15} /> : <DeleteOutlineIcon sx={{ fontSize: 18 }} />}
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        </Box>
                                      </Box>
                                    ))}
                                    {memberOf.length > PAGE_SIZE && (
                                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5, borderTop: '1px solid #F5F5F7' }}>
                                        <Pagination
                                          count={Math.ceil(memberOf.length / PAGE_SIZE)}
                                          page={ofPage}
                                          onChange={(_, v) => setOfPage(v)}
                                          size="small"
                                          color="primary"
                                        />
                                      </Box>
                                    )}
                                  </>
                                )}
                              </Box>
                            </Grid>
                          </Grid>
                        )}
                      </TabPanel>

                      {/* ── Requests Tab ── */}
                      <TabPanel value={tab} index={1}>
                        {requests.length === 0 ? (
                          <EmptyState message="No pending requests." />
                        ) : (
                          <>
                            <Grid container spacing={2}>
                              {pagedRequests.map((r) => (
                                <Grid item xs={12} sm={6} key={r._id}>
                                  <RequestCard
                                    person={r.requesterId}
                                    relation={r.relation}
                                    actions={
                                      <>
                                        <Tooltip title="Accept">
                                          <span>
                                            <IconButton size="small" disabled={!!actionLoading} onClick={() => handleRespond(r._id, 'accept')} sx={{ color: '#2AAB6C' }}>
                                              {actionLoading === r._id + 'accept' ? <CircularProgress size={16} /> : <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />}
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <Tooltip title="Reject">
                                          <span>
                                            <IconButton size="small" disabled={!!actionLoading} onClick={() => handleRespond(r._id, 'reject')} sx={{ color: '#F36363' }}>
                                              {actionLoading === r._id + 'reject' ? <CircularProgress size={16} /> : <CancelOutlinedIcon sx={{ fontSize: 20 }} />}
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      </>
                                    }
                                  />
                                </Grid>
                              ))}
                            </Grid>
                            {requests.length > PAGE_SIZE && (
                              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Pagination
                                  count={Math.ceil(requests.length / PAGE_SIZE)}
                                  page={reqPage}
                                  onChange={(_, v) => setReqPage(v)}
                                  size="small"
                                  color="primary"
                                />
                              </Box>
                            )}
                          </>
                        )}
                      </TabPanel>

                      {/* ── Sent Tab ── */}
                      <TabPanel value={tab} index={2}>
                        {sentRequests.length === 0 ? (
                          <EmptyState message="No sent requests." onAdd={() => setAddOpen(true)} />
                        ) : (
                          <>
                            <Grid container spacing={2}>
                              {pagedSent.map((r) => (
                                <Grid item xs={12} sm={6} key={r._id}>
                                  <RequestCard
                                    person={r.memberId}
                                    relation={r.relation}
                                    badge={<StatusTag label={r.status} />}
                                  />
                                </Grid>
                              ))}
                            </Grid>
                            {sentRequests.length > PAGE_SIZE && (
                              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Pagination
                                  count={Math.ceil(sentRequests.length / PAGE_SIZE)}
                                  page={sentPage}
                                  onChange={(_, v) => setSentPage(v)}
                                  size="small"
                                  color="primary"
                                />
                              </Box>
                            )}
                          </>
                        )}
                      </TabPanel>
                    </>
                  )}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </AppLayout>

      {/* Remove Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontFamily: 'Poppins', pb: 1 }}>Remove Family Member</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ fontFamily: 'Poppins', color: '#5E5E5E' }}>
            Are you sure you want to remove <strong>{confirmTarget.name}</strong> from your family members?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <StyledSecondaryButton
            variant="outlined"
            onClick={() => setConfirmOpen(false)}
            style={{ width: 'auto', background: '#f5f5f5', color: '#555' }}
          >
            Cancel
          </StyledSecondaryButton>
          <StyledSecondaryButton
            variant="contained"
            onClick={confirmRemove}
            style={{ width: 'auto', background: '#F36363' }}
          >
            Remove
          </StyledSecondaryButton>
        </DialogActions>
      </Dialog>

      {/* Add Family Member Dialog */}
      <Dialog open={addOpen} onClose={() => { setAddOpen(false); setForm({ emailOrPhone: '', relation: '' }); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontFamily: 'Poppins' }}>Add Family Member</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2, fontFamily: 'Poppins' }}>
            Enter the email or phone number of the patient you want to add. They will receive a
            consent request and must accept before you can view their data.
          </Typography>
          <TextField
            label="Email or Phone Number"
            fullWidth
            value={form.emailOrPhone}
            onChange={(e) => setForm((f) => ({ ...f, emailOrPhone: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Relation"
            fullWidth
            select
            value={form.relation}
            onChange={(e) => setForm((f) => ({ ...f, relation: e.target.value }))}
          >
            {RELATIONS.map((r) => (
              <MenuItem key={r} value={r}>{r}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <StyledSecondaryButton
            variant="outlined"
            onClick={() => { setAddOpen(false); setForm({ emailOrPhone: '', relation: '' }); }}
            style={{ width: 'auto', background: '#f5f5f5', color: '#555' }}
          >
            Cancel
          </StyledSecondaryButton>
          <StyledSecondaryButton
            variant="contained"
            onClick={handleSendRequest}
            disabled={addLoading}
            style={{ width: 'auto' }}
          >
            {addLoading ? <CircularProgress size={18} style={{ color: '#fff' }} /> : 'Send Request'}
          </StyledSecondaryButton>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default FamilyMembers;
